/** Locale currency inferred from an asset's country (Israel → ILS/₪, United States → USD/$). */

export type AssetLocaleCurrency = {
  code: 'ILS' | 'USD';
  symbol: '₪' | '$';
};

/** API country ids observed in asset payloads (Israel is typically 1). */
export const ISRAEL_COUNTRY_ID = 1;
const ISRAEL_COUNTRY_IDS = new Set([ISRAEL_COUNTRY_ID]);
/** United States when the API exposes a stable numeric id (name matching still applies). */
const US_COUNTRY_IDS = new Set([2]);

const ISRAEL_COUNTRY_NAME_KEYS = new Set([
  'israel',
  'state of israel',
  'ישראל',
]);

const US_COUNTRY_NAME_KEYS = new Set([
  'united states',
  'united states of america',
  'usa',
  'us',
  'u.s.',
  'u.s.a.',
  'ארצות הברית',
  'ארה"ב',
]);

export const DEFAULT_ASSET_LOCALE_CURRENCY: AssetLocaleCurrency = {
  code: 'ILS',
  symbol: '₪',
};

function coerceNumericField(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Country id on list/detail asset rows (root, address, or nested country object). */
export function assetRecordCountryId(asset: Record<string, unknown>): number | null {
  const from = (o: Record<string, unknown>): number | null => {
    for (const k of ['country_id', 'CountryId', 'countryId'] as const) {
      const n = coerceNumericField(o[k]);
      if (n !== null) {
        return n;
      }
    }
    return null;
  };

  const root = from(asset);
  if (root !== null) {
    return root;
  }

  const addr = asset.address;
  if (addr !== null && typeof addr === 'object' && !Array.isArray(addr)) {
    const n = from(addr as Record<string, unknown>);
    if (n !== null) {
      return n;
    }
  }

  for (const nestedKey of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nestedKey];
    if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = from(nested as Record<string, unknown>);
      if (n !== null) {
        return n;
      }
    }
  }

  const countryObj = asset.country;
  if (countryObj !== null && typeof countryObj === 'object' && !Array.isArray(countryObj)) {
    const c = countryObj as Record<string, unknown>;
    return (
      coerceNumericField(c.id) ??
      coerceNumericField(c.country_id) ??
      from(c)
    );
  }

  return null;
}

function assetRecordCountryNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.country_name);
  add(asset.countryName);
  if (typeof asset.country === 'string') {
    add(asset.country);
  }
  if (asset.country !== null && typeof asset.country === 'object' && !Array.isArray(asset.country)) {
    const c = asset.country as Record<string, unknown>;
    add(c.name);
    add(c.label);
    add(c.country_name);
    add(c.title);
  }
  const addr = asset.address;
  if (addr !== null && typeof addr === 'object' && !Array.isArray(addr)) {
    const a = addr as Record<string, unknown>;
    add(a.country);
    add(a.country_name);
    add(a.countryName);
  }
  return bag;
}

function currencyFromCountryId(countryId: number): AssetLocaleCurrency | null {
  if (ISRAEL_COUNTRY_IDS.has(countryId)) {
    return { code: 'ILS', symbol: '₪' };
  }
  if (US_COUNTRY_IDS.has(countryId)) {
    return { code: 'USD', symbol: '$' };
  }
  return null;
}

function currencyFromCountryNames(names: Set<string>): AssetLocaleCurrency | null {
  const list = Array.from(names);
  if (
    list.some((n) => ISRAEL_COUNTRY_NAME_KEYS.has(n) || n.includes('israel'))
  ) {
    return { code: 'ILS', symbol: '₪' };
  }
  if (
    list.some(
      (n) =>
        US_COUNTRY_NAME_KEYS.has(n) ||
        n.includes('united states') ||
        n === 'usa' ||
        n.startsWith('u.s.'),
    )
  ) {
    return { code: 'USD', symbol: '$' };
  }
  return null;
}

/** Resolve display currency from asset country id / names; defaults to Israel (ILS). */
export function resolveAssetLocaleCurrency(asset: Record<string, unknown>): AssetLocaleCurrency {
  const id = assetRecordCountryId(asset);
  if (id !== null) {
    const fromId = currencyFromCountryId(id);
    if (fromId) {
      return fromId;
    }
  }
  const fromNames = currencyFromCountryNames(assetRecordCountryNameBag(asset));
  if (fromNames) {
    return fromNames;
  }
  return DEFAULT_ASSET_LOCALE_CURRENCY;
}

export function labelWithCurrencySymbol(label: string, currency: AssetLocaleCurrency): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return label;
  }
  if (trimmed.includes(`(${currency.symbol})`)) {
    return trimmed;
  }
  return `${trimmed} (${currency.symbol})`;
}

export function isPriceRelatedFieldKey(key: string): boolean {
  const lower = key.trim().toLowerCase().replace(/^[_]+/, '').replace(/\s+/g, '_');
  if (/(^|_)price$/.test(lower) || /^price\b/.test(lower)) {
    return true;
  }
  return (
    lower === 'amount' ||
    lower === 'cost' ||
    lower === 'rate' ||
    lower === 'fee' ||
    lower === 'unit_price' ||
    lower === 'addon_price'
  );
}

function formatGroupedNumber(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 }).format(n);
}

/** Format a monetary amount with the locale currency symbol (e.g. ₪100, $50). */
export function formatMoneyAmount(amount: number, currency: AssetLocaleCurrency): string {
  if (!Number.isFinite(amount)) {
    return '—';
  }
  return `${currency.symbol}${formatGroupedNumber(amount)}`;
}
